import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { getApiUrl } from "@/lib/apiConfig";
import { AlertCircle, RefreshCw } from "lucide-react";

type Props = {
  children: ReactNode;
};

async function pingBackend() {
  const url = `${getApiUrl()}?action=test`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('Backend unreachable');
  return res.json();
}

export default function ApiHealthGate({ children }: Props) {
  const qc = useQueryClient();

  const {
    data,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ["backend-health"],
    queryFn: pingBackend,
    staleTime: 60_000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const backendOk = !!data && (data.success === true || data?.status === "ok");

  // Always render children — show a non-blocking warning banner if backend is down
  return (
    <>
      {!isLoading && (isError || !backendOk) && (
        <div className="sticky top-0 z-[100] bg-amber-500 text-white px-4 py-2 flex items-center justify-between text-sm shadow-md">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>Backend server unavailable — some features may not work. Please re-upload <strong>bridgeapi/api.php</strong> to your server.</span>
          </div>
          <button
            onClick={() => {
              qc.invalidateQueries({ queryKey: ["backend-health"] });
              refetch();
            }}
            disabled={isFetching}
            className="flex items-center gap-1 bg-white/20 hover:bg-white/30 rounded px-3 py-1 text-xs font-medium transition-colors"
          >
            <RefreshCw className={`w-3 h-3 ${isFetching ? "animate-spin" : ""}`} />
            Retry
          </button>
        </div>
      )}
      {children}
    </>
  );
}
